import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CollaborationPackageActions } from '@app/core/state/collaboration-packages/collaboration-packages.actions';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { DialogData, DialogResult } from '@app/shared/components/form-dialog-base/form-dialog-base.component';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { CountryType, isSweden } from 'src/app/core/entity/locale';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { TranslatableStringType } from 'src/app/core/i18n/types';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';

interface Data extends DialogData {
  numberOfMissingCollaborationPackages: number;
  actingOnMarket: CountryType;
}

type MissingCollaborationPackagesInfoDialogTranslations = {
  manageClientsMissingPackages: TranslatableStringType;
};

const TRANSLATION_SCOPE = 'missingCollaborationPackagesInfoDialogComponent';

@Component({
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <p
        *ngIf="isSwedishMarket"
        [innerHTML]="t('seMissingMandatoryPackagesText', { numberOfMissingCollaborationPackages }) | safeHtml"></p>
      <p
        *ngIf="!isSwedishMarket"
        [innerHTML]="t('nlMissingMandatoryPackagesText', { numberOfMissingCollaborationPackages }) | safeHtml"></p>
      <div class="mt-6 flex">
        <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.52187 7.9125C2.96875 8.38125 3.0625 9.0875 2.75625 9.65625C2.72187 9.71875 2.6875 9.78125 2.65625 9.84062C2.57812 9.98125 2.49375 10.1219 2.40937 10.2656C2.94375 10.125 3.46875 9.93125 3.96875 9.70312C4.10313 9.64375 4.23438 9.58125 4.3625 9.51562C4.65938 9.36563 4.99688 9.32187 5.32188 9.38437C5.7 9.45937 6.09688 9.49687 6.50313 9.49687C9.50937 9.49687 11.5031 7.48125 11.5031 5.49687C11.5031 3.5125 9.50937 1.49688 6.50313 1.49688C3.49688 1.49688 1.5 3.51562 1.5 5.5C1.5 6.375 1.85625 7.21562 2.52187 7.9125ZM13 5.5C13 8.5375 10.0906 11 6.5 11C5.99375 11 5.50312 10.95 5.02812 10.8594C4.88437 10.9312 4.73438 11.0031 4.58437 11.0719C3.45312 11.5844 2.09375 12 0.75 12C0.45 12 0.18125 11.8219 0.0625 11.5469C-0.05625 11.2719 0 10.9531 0.20625 10.7375C0.65 10.25 1.025 9.68437 1.35 9.10938C1.37813 9.05625 1.40937 9.00313 1.4375 8.95C0.5375 8.00313 0 6.80625 0 5.5C0 2.4625 2.90937 0 6.5 0C10.0906 0 13 2.4625 13 5.5ZM7.6625 11.9219C8.2 11.8469 8.725 11.725 9.225 11.5531C10.0625 12.6719 11.5719 13.5 13.5 13.5C13.9094 13.5 14.3062 13.4594 14.6812 13.3875C15.0062 13.325 15.3438 13.3688 15.6406 13.5188C15.7688 13.5844 15.9 13.6469 16.0344 13.7063C16.5344 13.9313 17.0625 14.1281 17.5938 14.2688C17.5063 14.125 17.425 13.9844 17.3469 13.8438C17.3125 13.7844 17.2781 13.7219 17.2469 13.6594C16.9406 13.0875 17.0344 12.3844 17.4813 11.9156C18.1438 11.2156 18.5 10.375 18.5 9.5C18.5 7.62813 16.725 5.72813 14 5.51875V5.5C14 4.98125 13.9312 4.48125 13.8062 4.00625C17.2531 4.14062 20 6.55 20 9.5C20 10.8062 19.4625 12.0031 18.5656 12.9469C18.5938 13 18.625 13.0562 18.6531 13.1062C18.975 13.6812 19.35 14.2469 19.7969 14.7344C20.0031 14.9531 20.0562 15.2719 19.9406 15.5437C19.8219 15.8187 19.55 15.9969 19.2531 15.9969C17.9094 15.9969 16.55 15.5813 15.4187 15.0688C15.2688 15 15.1188 14.9281 14.975 14.8562C14.5031 14.95 14.0094 14.9969 13.5031 14.9969C10.9406 14.9969 8.72188 13.7406 7.66563 11.9187L7.6625 11.9219ZM4.62187 3.50312C4.79375 3.02187 5.25 2.7 5.75938 2.7H7.05C7.80625 2.7 8.41562 3.3125 8.41562 4.06563C8.41562 4.55625 8.15312 5.00625 7.72813 5.25L7 5.66875V5.67812C7 5.95312 6.775 6.17812 6.5 6.17812C6.225 6.17812 6 5.95312 6 5.67812V5.38125C6 5.20312 6.09688 5.0375 6.25 4.94688L7.23125 4.38438C7.34375 4.31875 7.41563 4.19688 7.41563 4.06563C7.41563 3.8625 7.25 3.7 7.05 3.7H5.75938C5.67188 3.7 5.59375 3.75625 5.5625 3.8375L5.55312 3.86562C5.45937 4.125 5.175 4.2625 4.91563 4.16875C4.65625 4.075 4.51875 3.79063 4.6125 3.53125L4.62187 3.50312ZM5.79063 7.625C5.78418 7.52809 5.79771 7.4309 5.83036 7.33943C5.86302 7.24796 5.91411 7.16418 5.98046 7.09326C6.04682 7.02235 6.12703 6.96581 6.21613 6.92716C6.30523 6.88851 6.40132 6.86857 6.49844 6.86857C6.59556 6.86857 6.69164 6.88851 6.78074 6.92716C6.86984 6.96581 6.95005 7.02235 7.01641 7.09326C7.08277 7.16418 7.13386 7.24796 7.16651 7.33943C7.19917 7.4309 7.21269 7.52809 7.20625 7.625C7.21269 7.72191 7.19917 7.8191 7.16651 7.91057C7.13386 8.00204 7.08277 8.08582 7.01641 8.15674C6.95005 8.22765 6.86984 8.28419 6.78074 8.32284C6.69164 8.36149 6.59556 8.38143 6.49844 8.38143C6.40132 8.38143 6.30523 8.36149 6.21613 8.32284C6.12703 8.28419 6.04682 8.22765 5.98046 8.15674C5.91411 8.08582 5.86302 8.00204 5.83036 7.91057C5.79771 7.8191 5.78418 7.72191 5.79063 7.625Z"
            fill="var(--action-primary)" />
        </svg>

        <a *ngIf="isSwedishMarket" href="https://forum.bjornlunden.se/" target="_blank" class="ml-2">{{
          t('getHelpFromSupport')
        }}</a>
        <a *ngIf="!isSwedishMarket" href="https://support.nl.bjornlunden.com/" target="_blank" class="ml-2">{{
          t('getHelpFromSupport')
        }}</a>
      </div>
      <div class="flex justify-end mt-6">
        <bl-frontend-button
          class="mr-2"
          [text]="t('selectLater')"
          variant="secondary"
          (onclick)="close(null)"></bl-frontend-button>
        <bl-frontend-button [text]="t('selectNow')" variant="primary" (onclick)="next()"></bl-frontend-button>
      </div>
    </ng-container>
  `,
  standalone: true,
  imports: [CommonModule, FormsModule, BlFrontendButtonComponent, InputTextModule, SafeHtmlPipe, TranslocoModule],
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
})
export class MissingCollaborationPackagesInfoDialogComponent extends TranslatableFormDialogBaseComponent<
  Data,
  DialogResult
> {
  get numberOfMissingCollaborationPackages(): number {
    return this.config.data?.numberOfMissingCollaborationPackages;
  }

  get actingOnMarket(): CountryType {
    return this.config.data?.actingOnMarket;
  }

  get isSwedishMarket(): boolean {
    return isSweden(this.actingOnMarket);
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: MissingCollaborationPackagesInfoDialogTranslations,
  ): DynamicDialogConfig<DialogData> {
    return {
      header: translation.manageClientsMissingPackages,
      modal: true,
      dismissableMask: false,
      closeOnEscape: false,
      closable: false,
      draggable: false,
      width: '570px',
    };
  }

  protected override onAfterClose = () => {
    this.store.dispatch(CollaborationPackageActions.showingMissingCollaborationPackagesDialog({ visible: false }));
  };

  next(): void {
    this.close(null);
    this.router.navigateByUrl('/collaborationpackages');
  }
}
